<template>
  <div>
    <Head>
      <Meta
        name="description"
        content="Learn talking to ATC from the comfort of your home. With our simulators and courses the prices are low and the learning curve is high."
        />
      <Meta
        name="logo"
        content="/img/logo.png"
        />
      <Meta
        name="og:logo"
        content="/img/logo.png"
        />

      <Link
        rel="icon"
        type="image/png"
        href="/favicon-96x96.png"
        sizes="96x96" />
      <Link rel="shortcut icon" href="/favicon.ico" />
      <Link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <Link rel="manifest" href="/site.webmanifest" />
      <Html :lang="locale" />
    </Head>

    <Transition>
      <NuxtLayout>
        <ui-navigation />
        <ui-breadcrumbs v-if="isAuthed"/>
        <ui-navigation-bottom v-if="isAuthed" class="flex md:hidden" />
      </NuxtLayout>
    </Transition>

    <UniqueNotifications />
    <UniqueConsent />
    <UniqueSupport />

    <page-footer />
  </div>
</template>

<script setup lang="ts">
import '@/assets/css/tail.scss'
import moment from 'moment'
import { api } from '~/composables/Api'
import { ws } from '~/composables/Websocket.client'
import {
  load as loadUser,
  loadFromSocialite,
  isLoaded as userIsLoaded
} from '~/composables/UserState'
import { UniqueConsent } from '#components'

declare global {
  interface Window {
    api: typeof api;
    ws: typeof ws;
    stripe: any;
    setting: typeof setting;
    visitorId: typeof visitorId;
  }
}

const router = useRouter()
const { t, locale } = useI18n()

onMounted(async() => {
  useState('i18nT').value = t

  methodSetSitename()

  // if route has get soc=
  if (router.currentRoute.value?.query?.soc) {
    console.log('[app.v] SOC token found in query')
    await loadFromSocialite(router.currentRoute.value.query.soc as string)
  }

  if(localStorage.getItem('bearerToken')) {
    console.log('Bearer token found, setting it')
    api.setBearer(localStorage.getItem('bearerToken') as string)
  }else{
    userIsLoaded.value = true
  }

  window.api = api
  window.ws = ws
  window.visitorId = visitorId
  window.moment = moment
  window.setting = setting

  await api.xsrf()

  console.log('[app.v] Connecting to websocket')
  await ws.connect()

  if(localStorage.getItem('bearerToken')) {
    await loadUser()
    userIsLoaded.value = true
  }else{
    loadUser()
    userIsLoaded.value = true
  }

  // if current link contains id parameter, scroll to
  if (router.currentRoute.value.hash) {
    setTimeout(() => {
      console.log('[app.v] Hash found in current route on mount')
      const id = router.currentRoute.value.hash as string
      const element = document.getElementById(id.replace('#', ''))
      console.log('[app.v] Scrolling to element with id:', id)
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        })
      }
    }, 500)
  }

  setTimeout(() => {
    if(
      isAuthed.value
      && !user.value.email_verified
      && (useRoute().fullPath.indexOf('/complete-setup') === -1)
    ){
      console.log('[app.v] User not verified, showing notification')
      notify({
        type: 'warning',
        text: t('app.verify.text'),
        button: t('app.verify.button'),
        timeout: 0,
        buttonaction: () => {
          console.log('[app.v] Resending verification email')
          methodResendConfirmation()
        }
      })
    }
  },5000);

  console.log('[app.v] App mounted')
})

const methodSetSitename = () => {
  useHead({
    titleTemplate: (titleChunk) => {
      const siteName = t('appseo.sitename') // your i18n key
      const separator = '—'

      const title = titleChunk ? `${titleChunk} ${separator} ${siteName}` : siteName

      title.replace(/\s+/g, ' ').trim()

      return title
    }
  })
}

watch(() => router.currentRoute.value, (to) => {
  // if hash
  if (to.hash) {
    setTimeout(() => {
      console.log('[app.v] Hash found in route')
      const element = document.getElementById(to.hash.replace('#', ''))
      if (element) {
        console.log('[app.v] Scrolling to element with id:', to.hash)
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        })
      }else{
        console.log('[app.v] Element with id not found:', to.hash)
      }
    }, 500)
  }

  if(window?.location){
    let canonical = `${window?.location?.origin}${to.path}`

    // if canonical ends with a slash, remove it
    if (canonical.endsWith('/')) {
      canonical = canonical.slice(0, -1)
    }

    useHead({
      link: [
        {
          rel: 'canonical',
          href: canonical.toLowerCase(),
        },
      ],
    })
  }
}, { immediate: true })

// watch locale
watch(locale, (newLocale) => {
  console.log('[app.v] Locale changed to:', newLocale)
  methodSetSitename()
})
</script>
